/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const isIS: LocalizationResource = {
  locale: 'is-IS',
  apiKeys: {
    action__add: 'Bæta við lykli',
    action__search: 'Leita að lyklum',
    copySecret: {
      formButtonPrimary__copyAndClose: 'Afrita og loka',
      formHint: 'Af öryggisástæðum munum við ekki leyfa þér að skoða það aftur síðar.',
      formTitle: 'Afritaðu API-lykilinn "{{name}}" þinn núna',
    },
    createdAndExpirationStatus__expiresOn:
      "Búið til {{ createdDate | shortDate('is-IS') }} • Rennur út {{ expiresDate | longDate('is-IS') }}",
    createdAndExpirationStatus__never: "Búið til {{ createdDate | shortDate('is-IS') }} • Rennur aldrei út",
    detailsTitle__emptyRow: 'Engir API-lyklar fundust',
    formButtonPrimary__add: 'Búa til lykil',
    formFieldCaption__expiration__expiresOn: 'Rennur út {{ date }}',
    formFieldCaption__expiration__never: 'Þessi lykill rennur aldrei út',
    formFieldOption__expiration__180d: '180 dagar',
    formFieldOption__expiration__1d: '1 dagur',
    formFieldOption__expiration__1y: '1 ár',
    formFieldOption__expiration__30d: '30 dagar',
    formFieldOption__expiration__60d: '60 dagar',
    formFieldOption__expiration__7d: '7 dagar',
    formFieldOption__expiration__90d: '90 dagar',
    formFieldOption__expiration__never: 'Aldrei',
    formHint: 'Gefðu lyklinum nafn til að búa til nýjan. Þú getur afturkallað hann hvenær sem er.',
    formTitle: 'Bæta við nýjum API-lykli',
    lastUsed__days: 'f. {{days}}d.',
    lastUsed__hours: 'f. {{hours}}klst.',
    lastUsed__minutes: 'f. {{minutes}}mín.',
    lastUsed__months: 'f. {{months}}mán.',
    lastUsed__seconds: 'f. {{seconds}}sek.',
    lastUsed__years: 'f. {{years}}ár',
    menuAction__revoke: 'Afturkalla lykil',
    revokeConfirmation: {
      confirmationText: 'Afturkalla',
      formButtonPrimary__revoke: 'Afturkalla lykil',
      formHint: 'Ertu viss um að þú viljir eyða þessum leynilykli?',
      formTitle: 'Afturkalla leynilykil "{{apiKeyName}}"?',
      inputLabel: undefined,
    },
    tableHeader__actions: undefined,
    tableHeader__lastUsed: undefined,
    tableHeader__name: undefined,
  },
  backButton: 'Til baka',
  badge__activePlan: 'Virkt',
  badge__banned: undefined,
  badge__canceledEndsAt: "Aflýst • Lýkur {{ date | shortDate('is-IS') }}",
  badge__currentPlan: 'Núverandi áskrift',
  badge__default: 'Sjálfgefið',
  badge__endsAt: "Lýkur {{ date | shortDate('is-IS') }}",
  badge__expired: 'Útrunnið',
  badge__freeTrial: 'Ókeypis prufuáskrift',
  badge__otherImpersonatorDevice: 'Önnur tæki sem herma eftir',
  badge__pastDueAt: "Gjaldfallið {{ date | shortDate('is-IS') }}",
  badge__pastDuePlan: 'Gjaldfallið',
  badge__primary: 'Aðal',
  badge__renewsAt: "Endurnýjast {{ date | shortDate('is-IS') }}",
  badge__requiresAction: 'Krefst aðgerða',
  badge__startsAt: "Hefst {{ date | shortDate('is-IS') }}",
  badge__thisDevice: 'Þetta tæki',
  badge__trialEndsAt: "Prufuáskrift lýkur {{ date | shortDate('is-IS') }}",
  badge__unverified: 'Óstaðfest',
  badge__upcomingPlan: 'Væntanlegt',
  badge__userDevice: 'Notendatæki',
  badge__you: 'Þú',
  billing: {
    accountCredit: 'Reikningsinneign',
    addPaymentMethod__label: 'Bæta við greiðslumáta',
    alwaysFree: 'Alltaf ókeypis',
    annually: 'Árlega',
    availableFeatures: 'Tiltækir eiginleikar',
    billedAnnually: 'Greitt árlega',
    billedAnnuallyOnly: 'Aðeins greitt árlega',
    billedMonthly: undefined,
    billedMonthlyOnly: 'Aðeins greitt mánaðarlega',
    cancelFreeTrial: 'Hætta við prufuáskrift',
    cancelFreeTrialAccessUntil:
      "Prufuáskriftin þín verður virk til {{ date | longDate('is-IS') }}. Eftir það missir þú aðgang að prufueiginleikum. Þú verður ekki rukkuð/rukkaður.",
    cancelFreeTrialTitle: 'Hætta við prufuáskrift fyrir {{plan}} áskrift?',
    cancelSubscription: 'Hætta áskrift',
    cancelSubscriptionAccessUntil:
      "Þú getur haldið áfram að nota eiginleika '{{plan}}' til {{ date | longDate('is-IS') }}, eftir það muntu ekki lengur hafa aðgang.",
    cancelSubscriptionNoCharge: 'Þú verður ekki rukkuð/rukkaður fyrir þessa áskrift.',
    cancelSubscriptionPastDue:
      'Áskrift þín lýkur strax og þú munt missa aðgang að öllum eiginleikum áskriftarinnar. Þú verður beðin/beðinn um að greiða gjaldfallna upphæð við næstu áskrift.',
    cancelSubscriptionTitle: 'Hætta {{plan}} áskrift?',
    cannotSubscribeMonthly:
      'Þú getur ekki skráð þig í þessa áskrift með mánaðarlegri greiðslu. Til að skrá þig þarftu að velja árlega greiðslu.',
    cannotSubscribeUnrecoverable: 'Þú getur ekki skráð þig í þessa áskrift. Núverandi áskrift þín er dýrari en þessi.',
    checkout: {
      description__paymentSuccessful: 'Greiðsla þín tókst.',
      description__subscriptionSuccessful: 'Nýja áskriftin þín er tilbúin.',
      downgradeNotice:
        'Þú heldur núverandi áskrift og eiginleikum hennar til loka greiðslutímabilsins, síðan verður þú flutt yfir í þessa áskrift.',
      emailForm: {
        subtitle: 'Áður en þú getur lokið kaupunum þarftu að bæta við netfangi þar sem kvittanir verða sendar.',
        title: 'Bæta við netfangi',
      },
      lineItems: {
        title__freeTrialEndsAt: 'Prufuáskrift lýkur',
        title__paymentMethod: 'Greiðslumáti',
        title__statementId: 'Reikningsauðkenni',
        title__subscriptionBegins: 'Áskrift hefst',
        title__totalPaid: 'Samtals greitt',
      },
      pastDueNotice: 'Fyrri áskrift þín var gjaldfallin, án greiðslu.',
      perMonth: 'á mánuði',
      title: 'Greiðsla',
      title__paymentSuccessful: 'Greiðsla tókst!',
      title__subscriptionSuccessful: 'Tókst!',
      title__trialSuccess: 'Prufuáskrift hafin!',
      totalDueAfterTrial: 'Heildarupphæð eftir að prufuáskrift lýkur eftir {{days}} daga',
      totalDuePerPeriod: undefined,
    },
    credit: 'Inneign',
    creditRemainder: 'Inneign fyrir eftirstöðvar núverandi áskriftar.',
    defaultFreePlanActive: 'Þú ert á ókeypis áskrift',
    free: 'Ókeypis',
    getStarted: 'Byrja',
    highlightedPlanBadge: 'Vinsælt',
    keepFreeTrial: 'Halda prufuáskrift',
    keepSubscription: 'Halda áskrift',
    manage: 'Stjórna',
    manageSubscription: 'Stjórna áskrift',
    month: 'Mánuður',
    monthAbbreviation: 'mán.',
    monthPerUnit: 'Mánuður á {{unitName}}',
    monthly: 'Mánaðarlega',
    pastDue: 'Gjaldfallið',
    pay: 'Greiða {{amount}}',
    payerCreditRemainder: 'Inneign frá reikningsstöðu.',
    paymentMethod: {
      applePayDescription: {
        annual: 'Árleg greiðsla',
        monthly: 'Mánaðarleg greiðsla',
      },
      dev: {
        anyNumbers: 'Hvaða tölur sem er',
        cardNumber: 'Kortanúmer',
        cvcZip: 'CVC, póstnúmer',
        developmentMode: 'Þróunarhamur',
        expirationDate: 'Gildistími',
        testCardInfo: 'Prufukortaupplýsingar',
      },
    },
    paymentMethods__label: 'Greiðslumátar',
    pricingTable: {
      billingCycle: 'Greiðslutímabil',
      included: 'Innifalið',
      seatCost: {
        additionalSeats: '({{additionalTierFeePerBlockAmount}}/{{periodAbbreviation}} fyrir viðbótar)',
        freeUpToSeats: 'Ókeypis fyrir allt að {{endsAfterBlock}} sæti',
        includedSeats: '{{includedSeats}} sæti innifalin',
        perSeat: '{{feePerBlockAmount}}/{{periodAbbreviation}} á sæti',
        tooltip: {
          additionalSeatsEach: 'Viðbótarsæti eru {{feePerBlockAmount}}/{{period}} hvert.',
          firstSeatsIncludedInPlan: 'Fyrstu {{endsAfterBlock}} sætin eru innifalin í áskriftinni.',
          freeForUpToSeats: 'Ókeypis fyrir allt að {{endsAfterBlock}} sæti.',
        },
        unlimitedSeats: 'Ótakmörkuð sæti',
        upToSeats: 'Allt að {{endsAfterBlock}} sæti',
      },
    },
    proratedDiscount: undefined,
    prorationCredit: 'Hlutfallsleg inneign',
    reSubscribe: 'Endurnýja áskrift',
    seatBreakdownIncludedPlural: undefined,
    seatBreakdownIncludedSingular: undefined,
    seatBreakdownPlural: undefined,
    seatBreakdownSingular: undefined,
    seats: 'Sæti',
    seatsWithLimit: undefined,
    seeAllFeatures: 'Sjá alla eiginleika',
    startFreeTrial: 'Byrja prufuáskrift',
    startFreeTrial__days: 'Byrja {{days}} daga prufuáskrift',
    subscribe: 'Gerast áskrifandi',
    subscriptionDetails: {
      beginsOn: 'Hefst',
      currentBillingCycle: 'Núverandi greiðslutímabil',
      endsOn: 'Lýkur',
      firstPaymentAmount: 'Fyrsta greiðsluupphæð',
      firstPaymentOn: 'Fyrsta greiðsla',
      nextPaymentAmount: 'Næsta greiðsluupphæð',
      nextPaymentOn: 'Næsta greiðsla',
      pastDueAt: 'Gjaldfallið',
      renewsAt: 'Endurnýjast',
      subscribedOn: 'Skráð/skráður',
      title: 'Áskrift',
      trialEndsOn: 'Prufuáskrift lýkur',
      trialStartedOn: 'Prufuáskrift hófst',
    },
    subtotal: 'Millisamtala',
    subtotalRenewal: undefined,
    switchPlan: 'Skipta yfir í þessa áskrift',
    switchToAnnual: 'Skipta yfir í árlega',
    switchToAnnualWithAnnualPrice: 'Skipta yfir í árlega {{price}} / ár',
    switchToMonthly: 'Skipta yfir í mánaðarlega',
    switchToMonthlyWithPrice: 'Skipta yfir í mánaðarlega {{price}} / mánuð',
    totalDue: 'Samtals til greiðslu',
    totalDuePerPeriod: undefined,
    totalDueToday: 'Samtals til greiðslu í dag',
    viewFeatures: 'Sjá eiginleika',
    viewPayment: 'Sjá greiðslu',
    year: 'Ár',
    yearAbbreviation: 'ár',
    yearPerUnit: 'Ár á {{unitName}}',
  },
  configureSSO: {
    configureStep: {
      attributeMappingTable: {
        badges: {
          optional: undefined,
          required: undefined,
        },
      },
      samlCustom: {
        assignUsersStep: {
          headerSubtitle: undefined,
          paragraph: undefined,
          title: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attributeName: undefined,
              userProfile: undefined,
            },
            rows: {
              email: {
                attributeName: undefined,
                userProfile: undefined,
              },
              firstName: {
                attributeName: undefined,
                userProfile: undefined,
              },
              lastName: {
                attributeName: undefined,
                userProfile: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            paragraph: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
            title: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
      samlGoogle: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              appAttribute: undefined,
              googleAttribute: undefined,
            },
            rows: {
              email: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              firstName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              lastName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        configureUserAccess: {
          assignUsersInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          headerSubtitle: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataFile: {
            description: undefined,
            fileUploaded: undefined,
            label: undefined,
            removeFile: undefined,
            replaceFile: undefined,
            uploadFile: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataFile: undefined,
            title: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          nameIdInstructions: {
            step1: undefined,
            step2: undefined,
          },
          paragraph: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          title: undefined,
        },
      },
      samlMicrosoft: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attribute: undefined,
              claimName: undefined,
              value: undefined,
            },
            rows: {
              email: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              firstName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              lastName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
          step3: undefined,
          title: undefined,
        },
        createAppStep: {
          assignUsersInstructions: {
            paragraph1: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            step6: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: {
              label: undefined,
              subSteps: {
                appName: undefined,
                create: undefined,
                nonGallery: undefined,
              },
            },
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
            title: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          step1: undefined,
          step2: undefined,
          step3: undefined,
          step4: undefined,
          step5: undefined,
          step6: undefined,
          title: undefined,
        },
      },
      samlOkta: {
        assignUsersStep: {
          assignUsersInstructions: {
            paragraph: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              expression: undefined,
              name: undefined,
            },
            rows: {
              email: {
                expression: undefined,
                name: undefined,
              },
              firstName: {
                expression: undefined,
                name: undefined,
              },
              lastName: {
                expression: undefined,
                name: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        createAppStep: {
          completeSamlIntegrationInstructions: {
            step1: undefined,
            step2: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            serviceProviderFields: {
              acsUrl: {
                label: undefined,
              },
              spEntityId: {
                label: undefined,
              },
            },
            title: undefined,
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
            title: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
    },
    confirmation: {
      configurationSection: {
        configureAgainLink: undefined,
        issuerLabel: undefined,
        ssoUrlLabel: undefined,
        title: undefined,
      },
      domainSection: {
        title: undefined,
      },
      enableSection: {
        title: undefined,
      },
      inactiveBanner: {
        title: undefined,
      },
      resetSection: {
        confirmationFieldLabel: undefined,
        submitButton: undefined,
        title: undefined,
        warning: undefined,
      },
      statusSection: {
        activeBadge: undefined,
        inactiveBadge: undefined,
        title: undefined,
      },
    },
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'Hafðu samband við stjórnanda fyrirtækisins þíns til að uppfæra heimildir þínar.',
      title: 'Þú hefur ekki heimild til að stjórna einnar innskráningar (SSO)',
    },
    navbar: {
      title: 'Stilla einnar innskráningar (SSO)',
    },
    organizationDomainsStep: {
      domainCard: {
        badge__unverified: 'Óstaðfest',
        badge__verified: 'Staðfest',
        removeButtonTooltip__lastVerifiedDomain: undefined,
        removeButtonTooltip__lastVerifiedDomainActive: undefined,
        txtRecord: {
          hostLabel: 'Hýsill / Nafn',
          instructions:
            'Bættu þessari TXT-færslu við DNS-þjónustuna þína. Við staðfestum sjálfkrafa um leið og færslan verður virk.',
          typeLabel: 'Tegund',
          valueLabel: 'Gildi',
        },
        verifiedAtLabel: "Staðfest {{ date | shortDate('is-IS') }}",
      },
      domainSuggestion: {
        formButtonPrimary__add: 'Bæta við {{domain}}',
        messageLabel: 'Tölvupósturinn þinn notar {{domain}}. Viltu bæta því við?',
      },
      formButtonPrimary__add: 'Bæta við',
      formFieldInputPlaceholder__domain: 'Bæta við léni',
      formFieldLabel__domain: 'Lén',
      removeDomainDialog: {
        cancelButton: undefined,
        removeButton: undefined,
        subtitle__active: undefined,
        subtitle__inactive: undefined,
        title: undefined,
      },
      subtitle: 'Bættu við og staðfestu eignarhald á lénunum sem fyrirtækið þitt notar til að skrá sig inn.',
      title: 'Bæta við SSO-lénum',
    },
    resetConnectionDialog: {
      cancelButton: undefined,
      confirmationFieldLabel: undefined,
      confirmationFieldPlaceholder: undefined,
      resetButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    selectProviderStep: {
      saml: {
        customSaml: 'Sérsniðinn SAML þjónustuaðili',
        google: undefined,
        groupLabel: 'SAML',
        microsoft: undefined,
        okta: 'Okta Workforce',
      },
      subtitle: 'Veldu þjónustuaðilann sem þú ætlar að setja upp SSO fyrir.',
      title: 'Veldu þjónustuaðila',
      warning: 'Þegar þjónustuaðili hefur verið valinn er ekki hægt að breyta aftur fyrr en stillingu er lokið',
    },
    testConfigurationStep: {
      error__noSuccessfulTestRun: undefined,
      subtitle: undefined,
      testResults: {
        actionLabel__refresh: undefined,
        empty: {
          subtitle: undefined,
          title: undefined,
        },
        polling: undefined,
        status__failed: undefined,
        status__pending: undefined,
        status__success: undefined,
        title: undefined,
      },
      testRunDetails: {
        howToFix: {
          actionLabel__viewDocumentation: undefined,
          oauth_access_denied: {
            description: undefined,
          },
          oauth_fetch_user_error: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
          },
          oauth_token_exchange_error: {
            description: undefined,
          },
          saml_email_address_domain_mismatch: {
            description: undefined,
          },
          saml_response_relaystate_missing: {
            description: undefined,
          },
          saml_user_attribute_missing: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          sectionTitle: undefined,
        },
        parsedUserInfo: {
          email: undefined,
          firstName: undefined,
          sectionTitle: undefined,
        },
        runDetails: {
          actionLabel__copied: undefined,
          actionLabel__copy: undefined,
          errorCode: undefined,
          fullMessage: undefined,
          sectionTitle: undefined,
          status: undefined,
          timestamp: undefined,
        },
        title: undefined,
      },
      testUrl: {
        actionLabel__open: undefined,
      },
      title: undefined,
    },
  },
  createOrganization: {
    formButtonSubmit: 'Stofna samtök',
    invitePage: {
      formButtonReset: 'Sleppa',
    },
    title: 'Stofna samtök',
  },
  dates: {
    lastDay: "Í gær kl {{ date | timeString('is-IS') }}",
    next6Days: "{{ date | weekday('is-IS','long') }} kl {{ date | timeString('is-IS') }}",
    nextDay: "Á morgun kl {{ date | timeString('is-IS') }}",
    numeric: "{{ date | numeric('is-IS') }}",
    previous6Days: "Síðasta {{ date | weekday('is-IS','long') }} kl {{ date | timeString('is-IS') }}",
    sameDay: "Í dag kl {{ date | timeString('is-IS') }}",
  },
  dividerText: 'eða',
  footerActionLink__alternativePhoneCodeProvider: 'Senda kóða með SMS í staðinn',
  footerActionLink__useAnotherMethod: 'Nota aðra aðferð',
  footerPageLink__help: 'Hjálp',
  footerPageLink__privacy: 'Persónuvernd',
  footerPageLink__terms: 'Skilmálar',
  formButtonPrimary: 'Halda áfram',
  formButtonPrimary__verify: 'Staðfesta',
  formFieldAction__forgotPassword: 'Gleymt lykilorð?',
  formFieldError__matchingPasswords: 'Lykilorð passa saman.',
  formFieldError__notMatchingPasswords: 'Lykilorð passa ekki saman.',
  formFieldError__verificationLinkExpired: 'Staðfestingartengillinn er útrunninn. Vinsamlegast biðjið um nýjan tengil.',
  formFieldHintText__optional: 'Valfrjálst',
  formFieldHintText__slug:
    'Stubbur (e. slug) er auðlesanlegt auðkenni sem verður að vera einstakt. Það er oft notað í vefslóðum.',
  formFieldInputPlaceholder__apiKeyDescription: 'Útskýrðu hvers vegna þú ert að búa til þennan lykil',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'Veldu dagsetningu',
  formFieldInputPlaceholder__apiKeyName: 'Sláðu inn nafn leynilykils',
  formFieldInputPlaceholder__backupCode: 'Sláðu inn öryggiskóða',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Eyða aðgangi',
  formFieldInputPlaceholder__emailAddress: 'Sláðu inn netfangið þitt',
  formFieldInputPlaceholder__emailAddress_username: 'Sláðu inn netfang eða notendanafn',
  formFieldInputPlaceholder__emailAddresses: 'dæmi@netfang.is, dæmi2@netfang.is',
  formFieldInputPlaceholder__firstName: 'Fornafn',
  formFieldInputPlaceholder__lastName: 'Eftirnafn',
  formFieldInputPlaceholder__organizationDomain: 'daemi.is',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'thu@daemi.is',
  formFieldInputPlaceholder__organizationName: 'Nafn samtaka',
  formFieldInputPlaceholder__organizationSlug: 'min-samtok',
  formFieldInputPlaceholder__password: 'Sláðu inn lykilorðið þitt',
  formFieldInputPlaceholder__phoneNumber: 'Sláðu inn símanúmerið þitt',
  formFieldInputPlaceholder__signUpPassword: 'Búðu til lykilorð',
  formFieldInputPlaceholder__username: 'Sláðu inn notendanafnið þitt',
  formFieldInput__emailAddress_format: 'Dæmi um snið: nafn@daemi.is',
  formFieldLabel__apiKey: 'API-lykill',
  formFieldLabel__apiKeyDescription: 'Lýsing',
  formFieldLabel__apiKeyExpiration: 'Rennur út',
  formFieldLabel__apiKeyName: 'Nafn leynilykils',
  formFieldLabel__automaticInvitations: 'Virkja sjálfvirk boð fyrir þetta lén',
  formFieldLabel__backupCode: 'Öryggiskóði',
  formFieldLabel__confirmDeletion: 'Staðfesting',
  formFieldLabel__confirmPassword: 'Staðfesta lykilorð',
  formFieldLabel__currentPassword: 'Núverandi lykilorð',
  formFieldLabel__emailAddress: 'Netfang',
  formFieldLabel__emailAddress_username: 'Netfang eða notendanafn',
  formFieldLabel__emailAddresses: 'Netföng',
  formFieldLabel__firstName: 'Fornafn',
  formFieldLabel__lastName: 'Eftirnafn',
  formFieldLabel__newPassword: 'Nýtt lykilorð',
  formFieldLabel__organizationDomain: 'Lén',
  formFieldLabel__organizationDomainDeletePending: 'Eyða boðum í bið og tillögum',
  formFieldLabel__organizationDomainEmailAddress: 'Staðfestingarnetfang',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Sláðu inn netfang undir þessu léni til að fá kóða og staðfesta þetta lén.',
  formFieldLabel__organizationName: 'Nafn',
  formFieldLabel__organizationSlug: 'Stubbur (e. slug)',
  formFieldLabel__passkeyName: 'Nafn lykils',
  formFieldLabel__password: 'Lykilorð',
  formFieldLabel__phoneNumber: 'Símanúmer',
  formFieldLabel__role: 'Hlutverk',
  formFieldLabel__signOutOfOtherSessions: 'Skrá út af öllum öðrum tækjum',
  formFieldLabel__username: 'Notendanafn',
  identityPreviewEditButton__emailAddress: undefined,
  identityPreviewEditButton__identifier: undefined,
  identityPreviewEditButton__phoneNumber: undefined,
  impersonationFab: {
    action__signOut: 'Skrá út',
    title: 'Skráður inn sem {{identifier}}',
  },
  lastAuthenticationStrategy: 'Síðast notað',
  maintenanceMode: 'Við erum nú í viðhaldi, en ekki hafa áhyggjur, það ætti ekki að taka meira en nokkrar mínútur.',
  membershipRole__admin: 'Stjórnandi',
  membershipRole__basicMember: 'Meðlimur',
  membershipRole__guestMember: 'Gestur',
  oauthConsent: {
    action__allow: undefined,
    action__deny: undefined,
    offlineAccessNotice: undefined,
    redirectNotice: undefined,
    redirectUriModal: {
      subtitle: undefined,
      title: undefined,
    },
    scopeList: {
      title: undefined,
    },
    subtitle: undefined,
    viewFullUrl: undefined,
    warning: undefined,
  },
  organizationList: {
    action__createOrganization: 'Stofna samtök',
    action__invitationAccept: 'Ganga í',
    action__suggestionsAccept: 'Biðja um að ganga í',
    createOrganization: 'Stofna samtök',
    invitationAcceptedLabel: 'Gengið í',
    subtitle: 'til að halda áfram í {{applicationName}}',
    suggestionsAcceptedLabel: 'Bíður samþykkis',
    title: 'Veldu reikning',
    titleWithoutPersonal: 'Veldu samtök',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'API-lyklar',
    },
    badge__automaticInvitation: 'Sjálfvirk boð',
    badge__automaticSuggestion: 'Sjálfvirkar tillögur',
    badge__enterpriseSso: undefined,
    badge__manualInvitation: 'Engin sjálfvirk skráning',
    badge__unverified: 'Óstaðfest',
    billingPage: {
      paymentHistorySection: {
        empty: 'Engin greiðslusaga',
        notFound: 'Greiðslutilraun fannst ekki',
        tableHeader__amount: 'Upphæð',
        tableHeader__date: 'Dagsetning',
        tableHeader__status: 'Staða',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Gera sjálfgefið',
        actionLabel__remove: 'Fjarlægja',
        add: 'Bæta við nýjum greiðslumáta',
        addSubtitle: 'Bæta við nýjum greiðslumáta á reikninginn þinn.',
        cancelButton: 'Hætta við',
        formButtonPrimary__add: 'Bæta við greiðslumáta',
        formButtonPrimary__pay: 'Greiða {{amount}}',
        payWithTestCardButton: 'Greiða með prufukorti',
        removeMethod: {
          messageLine1: '{{identifier}} verður fjarlægt af þessum reikningi.',
          messageLine2:
            'Þú munt ekki lengur geta notað þennan greiðslumáta og allar endurteknar áskriftir sem byggja á honum munu hætta að virka.',
          successMessage: '{{paymentMethod}} hefur verið fjarlægt af reikningnum þínum.',
          title: 'Fjarlægja greiðslumáta',
        },
        title: 'Greiðslumátar',
      },
      start: {
        headerTitle__payments: 'Greiðslur',
        headerTitle__plans: 'Áskriftir',
        headerTitle__statements: 'Yfirlit',
        headerTitle__subscriptions: 'Áskrift',
      },
      statementsSection: {
        empty: 'Engin yfirlit til að birta',
        itemCaption__paidForPlan: 'Greitt fyrir {{plan}} {{period}} áskrift',
        itemCaption__payerCredit: 'Inneign frá reikningsstöðu',
        itemCaption__proratedCredit: 'Hlutfallsleg inneign fyrir hlutanotkun fyrri áskriftar',
        itemCaption__subscribedAndPaidForPlan: 'Skráð og greitt fyrir {{plan}} {{period}} áskrift',
        notFound: 'Yfirlit fannst ekki',
        tableHeader__amount: 'Upphæð',
        tableHeader__date: 'Dagsetning',
        title: 'Yfirlit',
        totalPaid: 'Samtals greitt',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Stjórna',
        actionLabel__newSubscription: 'Gerast áskrifandi',
        actionLabel__switchPlan: 'Skipta um áskrift',
        includedSeatsUsage: undefined,
        overview: undefined,
        paidSeatsUsage: undefined,
        seatLimit: undefined,
        seatLimitAndIncludedSeats: undefined,
        tableHeader__edit: 'Breyta',
        tableHeader__plan: 'Áskrift',
        tableHeader__startDate: 'Upphafsdagur',
        title: 'Áskrift',
      },
      subscriptionsSection: {
        actionLabel__default: 'Stjórna',
      },
      switchPlansSection: {
        title: 'Skipta um áskrift',
      },
      title: 'Greiðslur',
    },
    createDomainPage: {
      subtitle:
        'Bættu við léni til að staðfesta. Notendur með netföng undir þessu léni geta gengið í samtökin sjálfkrafa eða beðið um að ganga í.',
      title: 'Bæta við léni',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'Ekki tókst að senda boðin. Það eru þegar biðboð fyrir eftirfarandi netföng: {{email_addresses}}.',
      formButtonPrimary__continue: 'Senda boð',
      formButtonPrimary__purchaseSeats: undefined,
      selectDropdown__role: 'Veldu hlutverk',
      subtitle: 'Sláðu inn eða límdu eitt eða fleiri netföng, aðskilin með bilum eða kommum.',
      successMessage: 'Boð send með góðum árangri',
      title: 'Bjóða nýja meðlimi',
    },
    membersPage: {
      action__invite: 'Bjóða',
      action__search: 'Leita',
      activeMembersTab: {
        menuAction__remove: 'Fjarlægja meðlim',
        tableHeader__actions: 'Aðgerðir',
        tableHeader__joined: 'Gengið í',
        tableHeader__role: 'Hlutverk',
        tableHeader__user: 'Notandi',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: 'Við erum að uppfæra tiltæk hlutverk. Þegar því er lokið geturðu uppfært hlutverk aftur.',
          title: 'Hlutverk eru tímabundið læst',
        },
      },
      detailsTitle__emptyRow: 'Engir meðlimir til að sýna',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Bjóða notendum með því að tengja netfangalén við samtökin þín. Allir sem skrá sig með samsvarandi netfangalén geta gengið í samtökin hvenær sem er.',
          headerTitle: 'Sjálfvirk boð',
          primaryButton: 'Stjórna staðfestum lénum',
        },
        table__emptyRow: 'Engin boð til að sýna',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Afturkalla boð',
        tableHeader__invited: 'Boðið',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Notendur sem skrá sig með samsvarandi netfangalén, munu sjá tillögu um að biðja um að ganga í samtökin þín.',
          headerTitle: 'Sjálfvirkar tillögur',
          primaryButton: 'Stjórna staðfestum lénum',
        },
        menuAction__approve: 'Samþykkja',
        menuAction__reject: 'Hafna',
        tableHeader__requested: 'Beðið um aðgang',
        table__emptyRow: 'Engar beiðnir til að sýna',
      },
      start: {
        headerTitle__invitations: 'Boð',
        headerTitle__members: 'Meðlimir',
        headerTitle__requests: 'Beiðnir',
      },
    },
    navbar: {
      apiKeys: 'API-lyklar',
      billing: 'Greiðslur',
      description: 'Stjórna samtökunum þínum.',
      general: 'Almennt',
      members: 'Meðlimir',
      security: undefined,
      title: 'Samtök',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'Þú hefur ekki leyfi til að stjórna greiðslum fyrir þessi samtök.',
        planMembershipLimitExceeded:
          'Samtökin þín eru með {{count}} meðlimi (þ.m.t. boð í bið). Þessi áskrift leyfir aðeins {{limit}} meðlimi.',
      },
      title: 'Áskriftir',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Sláðu inn "{{organizationName}}" hér að neðan til að halda áfram.',
          messageLine1: 'Ertu viss um að þú viljir eyða þessum samtökum?',
          messageLine2: 'Þessi aðgerð er varanleg og óafturkræf.',
          successMessage: 'Þú hefur eytt samtökunum.',
          title: 'Eyða samtökum',
        },
        leaveOrganization: {
          actionDescription: 'Sláðu inn "{{organizationName}}" hér að neðan til að halda áfram.',
          messageLine1:
            'Ertu viss um að þú viljir yfirgefa þessi samtök? Þú munt missa aðgang að þessum samtökum og forritum þeirra.',
          messageLine2: 'Þessi aðgerð er varanleg og óafturkræf.',
          successMessage: 'Þú hefur yfirgefið samtökin.',
          title: 'Yfirgefa samtök',
        },
        title: 'Hætta',
      },
      domainSection: {
        menuAction__manage: 'Stjórna',
        menuAction__remove: 'Eyða',
        menuAction__verify: 'Staðfesta',
        primaryButton: 'Bæta við léni',
        subtitle:
          'Leyfa notendum að ganga í samtökin sjálfkrafa eða biðja um að ganga í byggt á staðfestu netfangaléni.',
        title: 'Staðfest lén',
      },
      successMessage: 'Samtökin hafa verið uppfærð.',
      title: 'Uppfæra prófíl',
    },
    removeDomainPage: {
      messageLine1: 'Netfangalén {{domain}} verður fjarlægt.',
      messageLine2: 'Notendur munu ekki geta gengið í samtökin sjálfkrafa eftir þetta.',
      successMessage: '{{domain}} hefur verið fjarlægt.',
      title: 'Fjarlægja lén',
    },
    securityPage: {
      removeDialog: {
        confirmButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
      ssoSection: {
        badge__active: undefined,
        badge__inProgress: undefined,
        badge__inactive: undefined,
        badge__unconfigured: undefined,
        descriptionLine1: undefined,
        domainLabel: undefined,
        menuAction__activate: undefined,
        menuAction__deactivate: undefined,
        menuAction__edit: undefined,
        menuAction__remove: undefined,
        primaryButton__continueConfiguration: undefined,
        primaryButton__startConfiguration: undefined,
        title: undefined,
      },
      title: undefined,
    },
    start: {
      headerTitle__general: 'Almennt',
      headerTitle__members: 'Meðlimir',
      membershipSeatUsageLabel: '{{count}} af {{limit}} sætum notuð',
      profileSection: {
        primaryButton: 'Uppfæra prófíl',
        title: 'Prófíll samtaka',
        uploadAction__title: 'Merki',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Að fjarlægja þetta lén mun hafa áhrif á boðna notendur.',
        removeDomainActionLabel__remove: 'Fjarlægja lén',
        removeDomainSubtitle: 'Fjarlægja þetta lén úr staðfestum lénum þínum',
        removeDomainTitle: 'Fjarlægja lén',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Notendur eru sjálfkrafa boðnir að ganga í samtökin þegar þeir skrá sig og geta gengið í hvenær sem er.',
        automaticInvitationOption__label: 'Sjálfvirk boð',
        automaticSuggestionOption__description:
          'Notendur fá tillögu um að biðja um að ganga í, en verða að vera samþykktir af stjórnanda áður en þeir geta gengið í samtökin.',
        automaticSuggestionOption__label: 'Sjálfvirkar tillögur',
        calloutInfoLabel: 'Að breyta skráningaraðferð mun aðeins hafa áhrif á nýja notendur.',
        calloutInvitationCountLabel: 'Biðboð send til notenda: {{count}}',
        calloutSuggestionCountLabel: 'Biðtillögur sendar til notenda: {{count}}',
        manualInvitationOption__description: 'Notendur geta aðeins verið boðnir handvirkt í samtökin.',
        manualInvitationOption__label: 'Engin sjálfvirk skráning',
        subtitle: 'Veldu hvernig notendur frá þessu léni geta gengið í samtökin.',
      },
      start: {
        headerTitle__danger: 'Hætta',
        headerTitle__enrollment: 'Skráningarmöguleikar',
      },
      subtitle: 'Lénið {{domain}} er nú staðfest. Haltu áfram með því að velja skráningarmáta.',
      title: 'Uppfæra {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Sláðu inn staðfestingarkóðann sem sendur var á netfangið þitt',
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'Lénið {{domainName}} þarf að vera staðfest með tölvupósti.',
      subtitleVerificationCodeScreen:
        'Staðfestingarkóði var sendur á {{emailAddress}}. Sláðu inn kóðann til að halda áfram.',
      title: 'Staðfesta lén',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: 'Loka samtakavali',
    action__createOrganization: 'Stofna samtök',
    action__invitationAccept: 'Ganga í',
    action__manageOrganization: 'Stjórna',
    action__openOrganizationSwitcher: 'Opna samtakaval',
    action__suggestionsAccept: 'Biðja um að ganga í',
    notSelected: 'Engin samtök valin',
    personalWorkspace: 'Persónulegur reikningur',
    suggestionsAcceptedLabel: 'Bíður samþykkis',
  },
  paginationButton__next: 'Næsta',
  paginationButton__previous: 'Fyrri',
  paginationRowText__displaying: 'Sýnir',
  paginationRowText__of: 'af',
  reverification: {
    alternativeMethods: {
      actionLink: 'Fá hjálp',
      actionText: 'Ertu ekki með neitt af þessu?',
      blockButton__backupCode: 'Nota öryggiskóða',
      blockButton__emailCode: 'Senda kóða á {{identifier}}',
      blockButton__passkey: 'Nota lykilinn þinn',
      blockButton__password: 'Halda áfram með lykilorði',
      blockButton__phoneCode: 'Senda SMS kóða á {{identifier}}',
      blockButton__totp: 'Nota auðkennisforritið þitt',
      getHelp: {
        blockButton__emailSupport: 'Senda tölvupóst á stuðning',
        content:
          'Ef þú átt í erfiðleikum með að staðfesta reikninginn þinn, sendu okkur tölvupóst og við munum vinna með þér til að endurheimta aðgang eins fljótt og auðið er.',
        title: 'Fá hjálp',
      },
      subtitle: 'Áttu í vandræðum? Þú getur notað einhverja af þessum aðferðum til staðfestingar.',
      title: 'Nota aðra aðferð',
    },
    backupCodeMfa: {
      subtitle: 'Sláðu inn öryggiskóðann sem þú fékkst þegar þú stilltir tveggja þrepa auðkenningu',
      title: 'Sláðu inn öryggiskóða',
    },
    emailCode: {
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'Sláðu inn kóðann sem sendur var á netfangið þitt til að halda áfram',
      title: 'Staðfesting nauðsynleg',
    },
    noAvailableMethods: {
      message: 'Ekki er hægt að halda áfram með staðfestingu. Engin viðeigandi auðkenningaraðferð er stillt.',
      subtitle: 'Villa kom upp',
      title: 'Ekki hægt að staðfesta reikninginn þinn',
    },
    passkey: {
      blockButton__passkey: 'Nota lykilinn þinn',
      subtitle:
        'Að nota lykilinn þinn staðfestir auðkenni þitt. Tækið þitt gæti beðið um fingrafar, andlit eða skjálás.',
      title: 'Nota lykilinn þinn',
    },
    password: {
      actionLink: 'Nota aðra aðferð',
      subtitle: 'Sláðu inn núverandi lykilorð til að halda áfram',
      title: 'Staðfesting nauðsynleg',
    },
    phoneCode: {
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'Sláðu inn kóðann sem sendur var á símann þinn til að halda áfram',
      title: 'Staðfesting nauðsynleg',
    },
    phoneCodeMfa: {
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'Sláðu inn kóðann sem sendur var á símann þinn til að halda áfram',
      title: 'Staðfesting nauðsynleg',
    },
    totpMfa: {
      formTitle: 'Staðfestingarkóði',
      subtitle: 'Sláðu inn kóðann sem auðkennisforritið þitt bjó til til að halda áfram',
      title: 'Staðfesting nauðsynleg',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Bæta við reikningi',
      action__signOutAll: 'Skrá út af öllum reikningum',
      subtitle: 'Veldu reikninginn sem þú vilt halda áfram með.',
      title: 'Veldu reikning',
    },
    alternativeMethods: {
      actionLink: 'Fá hjálp',
      actionText: 'Ertu ekki með neitt af þessu?',
      blockButton__backupCode: 'Nota öryggiskóða',
      blockButton__emailCode: 'Senda kóða á {{identifier}}',
      blockButton__emailLink: 'Senda tengil á {{identifier}}',
      blockButton__passkey: 'Skrá inn með lykli',
      blockButton__password: 'Skrá inn með lykilorði',
      blockButton__phoneCode: 'Senda SMS kóða á {{identifier}}',
      blockButton__totp: 'Nota auðkennisforritið þitt',
      getHelp: {
        blockButton__emailSupport: 'Senda tölvupóst á stuðning',
        content:
          'Ef þú átt í erfiðleikum með að skrá þig inn á reikninginn þinn, sendu okkur tölvupóst og við munum vinna með þér til að endurheimta aðgang eins fljótt og auðið er.',
        title: 'Fá hjálp',
      },
      subtitle: 'Áttu í vandræðum? Þú getur notað einhverja af þessum aðferðum til að skrá þig inn.',
      title: 'Nota aðra aðferð',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'til að halda áfram í {{applicationName}}',
      title: 'Athugaðu {{provider}}',
    },
    backupCodeMfa: {
      subtitle: 'Öryggiskóðinn þinn er sá sem þú fékkst þegar þú stilltir tveggja þrepa auðkenningu.',
      title: 'Sláðu inn öryggiskóða',
    },
    emailCode: {
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'til að halda áfram í {{applicationName}}',
      title: 'Athugaðu tölvupóstinn þinn',
    },
    emailCodeMfa: {
      formTitle: 'Athugaðu tölvupóstinn þinn',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'til að halda áfram í {{applicationName}}',
      title: 'Athugaðu tölvupóstinn þinn',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Til að halda áfram, opnaðu staðfestingartengilinn á tækinu og vafranum sem þú byrjaðir innskráninguna með',
        title: 'Staðfestingartengill er ógildur fyrir þetta tæki',
      },
      expired: {
        subtitle: 'Farðu aftur í upprunalega flipann til að halda áfram.',
        title: 'Þessi staðfestingartengill er útrunninn',
      },
      failed: {
        subtitle: 'Farðu aftur í upprunalega flipann til að halda áfram.',
        title: 'Þessi staðfestingartengill er ógildur',
      },
      formSubtitle: 'Nota staðfestingartengilinn sem sendur var á tölvupóstinn þinn',
      formTitle: 'Staðfestingartengill',
      loading: {
        subtitle: 'Þú verður fljótlega vísað áfram',
        title: 'Skrá inn...',
      },
      resendButton: 'Fékkstu ekki tengil? Senda aftur',
      subtitle: 'til að halda áfram í {{applicationName}}',
      title: 'Athugaðu tölvupóstinn þinn',
      unusedTab: {
        title: 'Þú getur lokað þessum flipa',
      },
      verified: {
        subtitle: 'Þú verður fljótlega vísað áfram',
        title: 'Tókst að skrá inn',
      },
      verifiedSwitchTab: {
        subtitle: 'Farðu aftur í upprunalega flipann til að halda áfram',
        subtitleNewTab: 'Farðu aftur í nýopnaða flipann til að halda áfram',
        titleNewTab: 'Skráður inn á öðrum flipa',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Notaðu staðfestingartengilinn sem sendur var á tölvupóstinn þinn',
      resendButton: 'Fékkstu ekki tengilinn? Senda aftur',
      subtitle: 'til að halda áfram í {{applicationName}}',
      title: 'Athugaðu tölvupóstinn þinn',
    },
    enterpriseConnections: {
      subtitle: 'Veldu fyrirtækjareikninginn sem þú vilt halda áfram með.',
      title: 'Veldu fyrirtækjareikning',
    },
    forgotPassword: {
      formTitle: 'Endurstilla lykilorð kóða',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'til að endurstilla lykilorðið þitt',
      subtitle_email: 'Fyrst, sláðu inn kóðann sem sendur var á netfangið þitt',
      subtitle_phone: 'Fyrst, sláðu inn kóðann sem sendur var á símann þinn',
      title: 'Endurstilla lykilorð',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Endurstilla lykilorðið þitt',
      label__alternativeMethods: 'Eða, skráðu þig inn með annarri aðferð',
      title: 'Gleymt lykilorð?',
    },
    newDeviceVerificationNotice:
      'Þú ert að skrá þig inn frá nýjum tæki. Við biðjum um staðfestingu til að halda reikningnum þínum öruggum.',
    noAvailableMethods: {
      message: 'Ekki er hægt að halda áfram með innskráningu. Engin tiltæk auðkenningaraðferð.',
      subtitle: 'Villa kom upp',
      title: 'Ekki hægt að skrá inn',
    },
    passkey: {
      subtitle:
        'Að nota lykilinn þinn staðfestir að þú ert það. Tækið þitt gæti beðið um fingrafar, andlit eða skjálás.',
      title: 'Nota lykilinn þinn',
    },
    password: {
      actionLink: 'Nota aðra aðferð',
      subtitle: 'Sláðu inn lykilorðið sem tengist reikningnum þínum',
      title: 'Sláðu inn lykilorðið þitt',
    },
    passwordCompromised: {
      title: 'Lykilorð brotið',
    },
    passwordPwned: {
      title: 'Lykilorð brotið',
    },
    passwordUntrusted: {
      title: 'Lykilorð óáreiðanlegt',
    },
    phoneCode: {
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'til að halda áfram í {{applicationName}}',
      title: 'Athugaðu símann þinn',
    },
    phoneCodeMfa: {
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'Til að halda áfram, vinsamlegast sláðu inn staðfestingarkóðann sem sendur var á símann þinn',
      title: 'Athugaðu símann þinn',
    },
    resetPassword: {
      formButtonPrimary: 'Endurstilla lykilorð',
      requiredMessage: 'Af öryggisástæðum er nauðsynlegt að endurstilla lykilorðið þitt.',
      successMessage: 'Lykilorðið þitt var endurstillt með góðum árangri. Skrá þig inn, vinsamlegast bíddu augnablik.',
      title: 'Setja nýtt lykilorð',
    },
    resetPasswordMfa: {
      detailsLabel: 'Við þurfum að staðfesta auðkenni þitt áður en við endurstillum lykilorðið þitt.',
    },
    start: {
      actionLink: 'Skrá sig',
      actionLink__join_waitlist: 'Skrá sig á biðlista',
      actionLink__use_email: 'Nota netfang',
      actionLink__use_email_username: 'Nota netfang eða notendanafn',
      actionLink__use_passkey: 'Nota lykil í staðinn',
      actionLink__use_phone: 'Nota síma',
      actionLink__use_username: 'Nota notendanafn',
      actionText: 'Ertu ekki með reikning?',
      actionText__join_waitlist: 'Viltu fá snemmaðgang?',
      alternativePhoneCodeProvider: {
        actionLink: 'Nota aðra aðferð',
        label: '{{provider}} símanúmer',
        subtitle: 'Sláðu inn símanúmerið þitt til að fá staðfestingarkóða á {{provider}}.',
        title: 'Skrá inn í {{applicationName}} með {{provider}}',
      },
      subtitle: 'Velkomin aftur! Vinsamlegast skráðu þig inn til að halda áfram',
      subtitleCombined: undefined,
      title: 'Skrá inn í {{applicationName}}',
      titleCombined: 'Halda áfram í {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Staðfestingarkóði',
      subtitle: 'Til að halda áfram, vinsamlegast sláðu inn staðfestingarkóðann sem auðkennisforritið þitt bjó til',
      title: 'Tveggja þrepa auðkenning',
    },
    web3Solana: {
      subtitle: 'Veldu veski hér að neðan til að skrá þig inn',
      title: 'Skrá inn með Solana',
    },
  },
  signInEnterPasswordTitle: 'Sláðu inn lykilorðið þitt',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'Sláðu inn staðfestingarkóðann sem sendur var á {{provider}}',
      title: 'Staðfesta {{provider}}',
    },
    continue: {
      actionLink: 'Skrá inn',
      actionText: 'Ertu með reikning?',
      subtitle: 'Vinsamlegast fylltu út eftirfarandi upplýsingar til að halda áfram.',
      title: 'Fylltu út vantar reiti',
    },
    emailCode: {
      formSubtitle: 'Sláðu inn staðfestingarkóðann sem sendur var á netfangið þitt',
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'Sláðu inn staðfestingarkóðann sem sendur var á netfangið þitt',
      title: 'Staðfesta netfang',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Til að halda áfram, opnaðu staðfestingartengilinn á tækinu og vafranum sem þú byrjaðir skráninguna með',
        title: 'Staðfestingartengill er ógildur fyrir þetta tæki',
      },
      formSubtitle: 'Nota staðfestingartengilinn sem sendur var á netfangið þitt',
      formTitle: 'Staðfestingartengill',
      loading: {
        title: 'Skrá sig...',
      },
      resendButton: 'Fékkstu ekki tengil? Senda aftur',
      subtitle: 'til að halda áfram í {{applicationName}}',
      title: 'Staðfesta netfang',
      verified: {
        title: 'Tókst að skrá sig',
      },
      verifiedSwitchTab: {
        subtitle: 'Farðu aftur í nýopnaða flipann til að halda áfram',
        subtitleNewTab: 'Farðu aftur í fyrri flipann til að halda áfram',
        title: 'Tókst að staðfesta netfang',
      },
    },
    enterpriseConnections: {
      subtitle: 'Veldu fyrirtækjareikninginn sem þú vilt halda áfram með.',
      title: 'Veldu fyrirtækjareikning',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Ég samþykki {{ privacyPolicyLink || link("Persónuverndarstefnuna") }}',
        label__onlyTermsOfService: 'Ég samþykki {{ termsOfServiceLink || link("Þjónustuskilmálana") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'Ég samþykki {{ termsOfServiceLink || link("Þjónustuskilmálana") }} og {{ privacyPolicyLink || link("Persónuverndarstefnuna") }}',
      },
      continue: {
        subtitle: 'Vinsamlegast lestu og samþykktu skilmálana til að halda áfram',
        title: 'Lagaleg samþykki',
      },
    },
    phoneCode: {
      formSubtitle: 'Sláðu inn staðfestingarkóðann sem sendur var á símanúmerið þitt',
      formTitle: 'Staðfestingarkóði',
      resendButton: 'Fékkstu ekki kóða? Senda aftur',
      subtitle: 'Sláðu inn staðfestingarkóðann sem sendur var á símanúmerið þitt',
      title: 'Staðfesta símanúmer',
    },
    restrictedAccess: {
      actionLink: 'Skrá inn',
      actionText: 'Ertu nú þegar með reikning?',
      blockButton__emailSupport: 'Senda tölvupóst á stuðning',
      blockButton__joinWaitlist: 'Skrá sig á biðlista',
      subtitle: 'Nýskráning er lokað. Ef þú telur þig eiga aðgang, vinsamlegast hafðu samband við stuðning.',
      subtitleWaitlist:
        'Nýskráning er lokað. Til að vera fyrst/fyrsta til að vita þegar við opnum, skráðu þig á biðlista.',
      title: 'Aðgangur takmarkaður',
    },
    start: {
      actionLink: 'Skrá inn',
      actionLink__use_email: 'Nota netfang í staðinn',
      actionLink__use_phone: 'Nota síma í staðinn',
      actionText: 'Ertu með reikning?',
      alternativePhoneCodeProvider: {
        actionLink: 'Nota aðra aðferð',
        label: '{{provider}} símanúmer',
        subtitle: 'Sláðu inn símanúmerið þitt til að fá staðfestingarkóða á {{provider}}.',
        title: 'Skráðu þig í {{applicationName}} með {{provider}}',
      },
      subtitle: 'Velkomin! Vinsamlegast fylltu út upplýsingar til að byrja.',
      subtitleCombined: 'Velkomin! Vinsamlegast fylltu út upplýsingar til að byrja.',
      title: 'Stofna reikning',
      titleCombined: 'Stofna reikning',
    },
    web3Solana: {
      subtitle: 'Veldu veski hér að neðan til að skrá þig',
      title: 'Skráðu þig með Solana',
    },
  },
  socialButtonsBlockButton: 'Halda áfram með {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'Fyrirtæki er þegar til fyrir uppgötvaða fyrirtækjanafnið ({{organizationName}}) og {{organizationDomain}}. Skráðu þig með boði.',
    },
    chooseOrganization: {
      action__createOrganization: 'Stofna samtök',
      action__invitationAccept: 'Ganga í',
      action__suggestionsAccept: 'Biðja um að ganga í',
      subtitle: 'Gangtu í fyrirliggjandi samtök eða stofnaðu ný',
      subtitle__createOrganizationDisabled: 'Gangtu í fyrirliggjandi samtök',
      suggestionsAcceptedLabel: 'Bíður eftir samþykki',
      title: 'Veldu samtök',
    },
    createOrganization: {
      formButtonReset: 'Hætta við',
      formButtonSubmit: 'Halda áfram',
      formFieldInputPlaceholder__name: 'Samtökin mín',
      formFieldInputPlaceholder__slug: 'samtokin-min',
      formFieldLabel__name: 'Nafn',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Sláðu inn upplýsingar um samtökin þín til að halda áfram',
      title: 'Stilltu samtökin þín',
    },
    organizationCreationDisabled: {
      subtitle: 'Hafðu samband við stjórnanda samtakanna til að fá boð.',
      title: 'Þú verður að tilheyra samtökum',
    },
    signOut: {
      actionLink: 'Skrá út',
      actionText: 'Skráður inn sem {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: 'Endurstilla lykilorð',
    signOut: {
      actionLink: 'Skrá út',
      actionText: 'Skráður inn sem {{identifier}}',
    },
    subtitle: 'Reikningurinn þinn krefst nýs lykilorðs áður en þú getur haldið áfram',
    title: 'Endurstilla lykilorðið þitt',
  },
  taskSetupMfa: {
    badge: 'Uppsetning tveggja þrepa auðkenningar',
    signOut: {
      actionLink: 'Skrá út',
      actionText: 'Skráður inn sem {{identifier}}',
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: 'Halda áfram',
        infoText:
          'SMS sem inniheldur staðfestingarkóða verður sent á þetta símanúmer. Skilaboð og gagnagjöld geta átt við.',
      },
      addPhoneNumber: 'Bæta við símanúmeri',
      cancel: 'Hætta við',
      subtitle: 'Veldu símanúmer sem þú vilt nota fyrir SMS kóða tveggja þrepa auðkenningu',
      success: {
        finishButton: 'Halda áfram',
        message1:
          'Tveggja þrepa auðkenning er nú virkjað. Við innskráningu þarftu að slá inn staðfestingarkóða sem sendur er á þetta símanúmer sem viðbótar skref.',
        message2:
          'Vistaðu þessa öryggiskóða og geymdu þá á öruggum stað. Ef þú missir aðgang að auðkennis tækinu þínu, getur þú notað öryggiskóða til að skrá þig inn.',
        title: 'SMS kóða staðfesting virkjuð',
      },
      title: 'Bæta við SMS kóða staðfestingu',
      verifyPhone: {
        formButtonPrimary: 'Halda áfram',
        formTitle: 'Staðfestingarkóði',
        resendButton: 'Fékkstu ekki kóða? Senda aftur',
        subtitle: 'Sláðu inn staðfestingarkóðann sem sendur var á',
        title: 'Staðfesta símanúmerið þitt',
      },
    },
    start: {
      methodSelection: {
        phoneCode: 'SMS kóði',
        totp: 'Auðkennisforrit',
      },
      subtitle: 'Veldu hvaða aðferð þú vilt nota til að vernda reikninginn þinn með auknu öryggi',
      title: 'Setja upp tveggja þrepa auðkenningu',
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Skanna QR kóða í staðinn',
        buttonUnableToScan__nonPrimary: 'Getur ekki skannað QR kóða?',
        formButtonPrimary: 'Halda áfram',
        formButtonReset: 'Hætta við',
        infoText__ableToScan:
          'Settu upp nýja innskráningaraðferð í auðkennisforritinu þínu og skannaðu eftirfarandi QR kóða til að tengja það við reikninginn þinn.',
        infoText__unableToScan:
          'Settu upp nýja innskráningaraðferð í auðkennisforritinu þínu og sláðu inn lykilinn hér að neðan.',
        inputLabel__unableToScan1:
          'Gakktu úr skugga um að Tímatengdir eða Einnota lykilorð séu virkjuð, og ljúktu síðan við að tengja reikninginn þinn.',
      },
      success: {
        finishButton: 'Halda áfram',
        message1:
          'Tveggja þrepa auðkenning er nú virkjað. Við innskráningu þarftu að slá inn staðfestingarkóða frá þessu auðkennisforriti sem viðbótar skref.',
        message2:
          'Vistaðu þessa öryggiskóða og geymdu þá á öruggum stað. Ef þú missir aðgang að auðkennis tækinu þínu, getur þú notað öryggiskóða til að skrá þig inn.',
        title: 'Staðfesting auðkennisforrits virkjuð',
      },
      title: 'Bæta við auðkennisforriti',
      verifyTotp: {
        formButtonPrimary: 'Halda áfram',
        formButtonReset: 'Hætta við',
        formTitle: 'Staðfestingarkóði',
        subtitle: 'Sláðu inn staðfestingarkóðann sem auðkennisforritið þitt bjó til',
        title: 'Bæta við auðkennisforriti',
      },
    },
  },
  unstable__errors: {
    already_a_member_in_organization: '{{email}} er þegar meðlimur í samtökunum.',
    api_key_name_already_exists: 'API-lyklanafn er þegar til.',
    api_key_usage_exceeded:
      'Þú hefur náð notkunarmörkum þínum. Þú getur fjarlægt mörkin með því að uppfæra í greidda áskrift.',
    avatar_file_size_exceeded: 'Skráarstærð fer yfir hámarksmörk 10 MB. Vinsamlegast veldu minni skrá.',
    avatar_file_type_invalid: 'Skráargerð er ekki studd. Vinsamlegast hlaðið upp JPG, PNG, GIF eða WEBP mynd.',
    captcha_invalid:
      'Skráning mistókst vegna misheppnaðra öryggisstaðfestinga. Vinsamlegast endurhlaðið síðuna til að reyna aftur eða hafið samband við stuðning til að fá frekari aðstoð.',
    captcha_unavailable:
      'Skráning mistókst vegna misheppnaðrar vélmenna staðfestingar. Vinsamlegast endurhlaðið síðuna til að reyna aftur eða hafið samband við stuðning til að fá frekari aðstoð.',
    form_code_incorrect: undefined,
    form_email_address_blocked:
      'Tímabundin tölvupóstþjónusta er ekki studd. Vinsamlegast notaðu venjulega netfangið þitt til að búa til reikning.',
    form_identifier_exists__email_address: 'Þetta netfang er þegar í notkun. Vinsamlegast reyndu annað.',
    form_identifier_exists__phone_number: 'Þetta símanúmer er þegar í notkun. Vinsamlegast reyndu annað.',
    form_identifier_exists__username: 'Þetta notendanafn er þegar í notkun. Vinsamlegast reyndu annað.',
    form_identifier_not_found: 'Við getum ekki fundið reikning með þessum upplýsingum.',
    form_new_password_matches_current: 'Nýtt lykilorð má ekki vera það sama og núverandi lykilorð.',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: 'Netfang verður að vera gilt netfang.',
    form_param_format_invalid__phone_number: 'Símanúmer verður að vera á giltu alþjóðlegu formi',
    form_param_max_length_exceeded__first_name: 'Fornafn má ekki vera lengra en 256 stafir.',
    form_param_max_length_exceeded__last_name: 'Eftirnafn má ekki vera lengra en 256 stafir.',
    form_param_max_length_exceeded__name: 'Nafn má ekki vera lengra en 256 stafir.',
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: 'Lykilorðið þitt er of stutt. Það verður að vera að minnsta kosti 8 stafir.',
    form_password_not_strong_enough: 'Lykilorðið þitt er ekki nógu sterkt.',
    form_password_or_identifier_incorrect: 'Lykilorðið eða netfangið er rangt. Reyndu aftur eða notaðu aðra aðferð.',
    form_password_pwned:
      'Þetta lykilorð hefur fundist sem hluti af öryggisbresti og má ekki nota, vinsamlegast reyndu annað lykilorð.',
    form_password_pwned__sign_in:
      'Þetta lykilorð hefur fundist sem hluti af öryggisbresti og má ekki nota, vinsamlegast endurstilltu lykilorðið þitt.',
    form_password_size_in_bytes_exceeded:
      'Lykilorðið þitt hefur farið yfir hámarksfjölda bæta sem leyfðir eru, vinsamlegast styttu það eða fjarlægðu nokkra sérstafi.',
    form_password_untrusted__sign_in: undefined,
    form_password_validation_failed: 'Rangt lykilorð',
    form_username_invalid_character: undefined,
    form_username_invalid_length:
      'Notendanafnið þitt verður að vera á milli {{min_length}} og {{max_length}} stafir að lengd.',
    form_username_needs_non_number_char:
      'Notandanafnið þitt verður að innihalda að minnsta kosti einn staf sem er ekki tala.',
    identification_deletion_failed: 'Þú getur ekki eytt síðasta auðkenni þínu.',
    insufficient_seats_change_plan: undefined,
    insufficient_seats_contact_support: undefined,
    not_allowed_access:
      "Netfang eða símanúmer þitt er ekki leyft til að skrá sig. Þetta gæti verið vegna þess að þú ert að nota '+', '=', '#' eða '.' í netfangi þínu, að nota domen sem tengist tímabundnum tölvupóstur, eða að þú ert búinn til að nota það. Ef þú reynir að skrá sig og færð villu, vinsamlegast hafið samband við stuðning.",
    oauth_access_denied: undefined,
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: 'Þú hefur náð hámarksfjölda samtakaaðilda, þar á meðal útistandandi boð.',
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized:
      'Þú ert ekki lengur meðlimur í þessum samtökum. Vinsamlegast veldu eða stofnaðu önnur samtök.',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      'Þú ert ekki lengur meðlimur í þessum samtökum. Vinsamlegast veldu önnur samtök.',
    passkey_already_exists: 'Lykill er þegar skráður með þessu tæki.',
    passkey_not_supported: 'Lyklar eru ekki studdir á þessu tæki.',
    passkey_pa_not_supported: 'Skráning krefst vettvangs auðkennis en tækið styður það ekki.',
    passkey_registration_cancelled: 'Skráning lykils var hætt eða rann út.',
    passkey_retrieval_cancelled: 'Staðfesting lykils var hætt eða rann út.',
    passwordComplexity: {
      maximumLength: 'minna en {{length}} stafir',
      minimumLength: '{{length}} eða fleiri stafir',
      requireLowercase: 'lágstaf',
      requireNumbers: 'tölu',
      requireSpecialCharacter: 'sérstaf',
      requireUppercase: 'hástaf',
      sentencePrefix: 'Lykilorðið þitt verður að innihalda',
    },
    phone_number_exists: 'Þetta símanúmer er þegar í notkun. Vinsamlegast reyndu annað.',
    session_exists: 'Þú ert nú þegar innskráður.',
    web3_missing_identifier: 'Ekki fannst Web3 veskisviðbót. Vinsamlegast settu upp eina til að halda áfram.',
    web3_signature_request_rejected: 'Þú hafðir hafnað beiðni um undirritun. Reyndu aftur til að halda áfram.',
    web3_solana_signature_generation_failed:
      'Villa kom upp við að búa til undirritun. Reyndu aftur til að halda áfram.',
    zxcvbn: {
      couldBeStronger: 'Lykilorðið þitt virkar, en gæti verið sterkara. Reyndu að bæta við fleiri stöfum.',
      goodPassword: 'Lykilorðið þitt uppfyllir allar nauðsynlegar kröfur.',
      notEnough: 'Lykilorðið þitt er ekki nógu sterkt.',
      suggestions: {
        allUppercase: 'Stórstafa sum, en ekki alla stafi.',
        anotherWord: 'Bættu við fleiri orðum sem eru minna algeng.',
        associatedYears: 'Forðastu ár sem tengjast þér.',
        capitalization: 'Stórstafa fleiri en fyrsta staf.',
        dates: 'Forðastu dagsetningar og ár sem tengjast þér.',
        l33t: "Forðastu fyrirsjáanlegar stafaskiptingar eins og '@' fyrir 'a'.",
        longerKeyboardPattern: 'Notaðu lengri lyklaborðsmynstur og breyttu sláttarstefnu nokkrum sinnum.',
        noNeed: 'Þú getur búið til sterk lykilorð án þess að nota tákn, tölur eða hástafi.',
        pwned: 'Ef þú notar þetta lykilorð annars staðar, ættir þú að breyta því.',
        recentYears: 'Forðastu nýleg ár.',
        repeated: 'Forðastu endurtekin orð og stafi.',
        reverseWords: 'Forðastu öfug stafsetning algengra orða.',
        sequences: 'Forðastu algengar stafaraðir.',
        useWords: 'Notaðu mörg orð, en forðastu algengar setningar.',
      },
      warnings: {
        common: 'Þetta er algengt lykilorð.',
        commonNames: 'Algeng nöfn og eftirnöfn eru auðvelt að giska á.',
        dates: 'Dagsetningar eru auðvelt að giska á.',
        extendedRepeat: 'Endurtekin stafamynstur eins og "abcabcabc" eru auðvelt að giska á.',
        keyPattern: 'Stutt lyklaborðsmynstur eru auðvelt að giska á.',
        namesByThemselves: 'Einstök nöfn eða eftirnöfn eru auðvelt að giska á.',
        pwned: 'Lykilorðið þitt var afhjúpað í öryggisbresti á netinu.',
        recentYears: 'Nýleg ár eru auðvelt að giska á.',
        sequences: 'Algengar stafaraðir eins og "abc" eru auðvelt að giska á.',
        similarToCommon: 'Þetta er svipað og algengt lykilorð.',
        simpleRepeat: 'Endurteknir stafir eins og "aaa" eru auðvelt að giska á.',
        straightRow: 'Beinar raðir af lyklum á lyklaborðinu eru auðvelt að giska á.',
        topHundred: 'Þetta er oft notað lykilorð.',
        topTen: 'Þetta er mjög oft notað lykilorð.',
        userInputs: 'Það ætti ekki að vera neinar persónulegar eða síðu tengdar upplýsingar.',
        wordByItself: 'Einstök orð eru auðvelt að giska á.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Bæta við reikningi',
    action__closeUserMenu: 'Loka notendavalmynd',
    action__manageAccount: 'Stjórna reikningi',
    action__openUserMenu: 'Opna notendavalmynd',
    action__signOut: 'Skrá út',
    action__signOutAll: 'Skrá út af öllum reikningum',
    label__userButtonPopover: 'Reikningsspjald',
    label__accountActions: 'Reikningsaðgerðir',
    label__activeSessions: 'Virkar lotur',
  },
  userProfile: {
    apiKeysPage: {
      title: 'API-lyklar',
    },
    backupCodePage: {
      actionLabel__copied: 'Afritað!',
      actionLabel__copy: 'Afrita allt',
      actionLabel__download: 'Sækja .txt',
      actionLabel__print: 'Prenta',
      infoText1: 'Öryggiskóðar verða virkjaðir fyrir þennan reikning.',
      infoText2:
        'Geymdu öryggiskóðana leynilega og geymdu þá á öruggum stað. Þú getur endurmyndað öryggiskóða ef þú grunar að þeir hafi verið afhjúpaðir.',
      subtitle__codelist: 'Geymdu þá á öruggum stað og haltu þeim leynilegum.',
      successMessage:
        'Öryggiskóðar eru nú virkjaðir. Þú getur notað einn af þessum til að skrá þig inn á reikninginn þinn, ef þú missir aðgang að auðkennis tækinu þínu. Hver kóði getur aðeins verið notaður einu sinni.',
      successSubtitle:
        'Þú getur notað einn af þessum til að skrá þig inn á reikninginn þinn, ef þú missir aðgang að auðkennis tækinu þínu.',
      title: 'Bæta við öryggiskóða staðfestingu',
      title__codelist: 'Öryggiskóðar',
    },
    billingPage: {
      paymentHistorySection: {
        empty: 'Engin greiðslusaga',
        notFound: 'Greiðslutilraun fannst ekki',
        tableHeader__amount: 'Upphæð',
        tableHeader__date: 'Dagsetning',
        tableHeader__status: 'Staða',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Gera sjálfgefið',
        actionLabel__remove: 'Fjarlægja',
        add: 'Bæta við nýjum greiðslumáta',
        addSubtitle: 'Bæta við nýjum greiðslumáta á reikninginn þinn.',
        cancelButton: 'Hætta við',
        formButtonPrimary__add: 'Bæta við greiðslumáta',
        formButtonPrimary__pay: 'Greiða {{amount}}',
        payWithTestCardButton: 'Greiða með prufukorti',
        removeMethod: {
          messageLine1: '{{identifier}} verður fjarlægt af þessum reikningi.',
          messageLine2:
            'Þú munt ekki lengur geta notað þessa greiðsluaðferð og allar endurteknar áskriftir sem byggja á henni munu hætta að virka.',
          successMessage: '{{paymentMethod}} hefur verið fjarlægt af reikningnum þínum.',
          title: 'Fjarlægja greiðslumáta',
        },
        title: 'Greiðslumátar',
      },
      start: {
        headerTitle__payments: 'Greiðslur',
        headerTitle__plans: 'Áskriftir',
        headerTitle__statements: 'Yfirlit',
        headerTitle__subscriptions: 'Áskrift',
      },
      statementsSection: {
        empty: 'Engin yfirlit til að birta',
        itemCaption__paidForPlan: 'Greitt fyrir {{plan}} {{period}} áskrift',
        itemCaption__payerCredit: 'Inneign frá reikningsstöðu',
        itemCaption__proratedCredit: 'Hlutfallsleg inneign fyrir hlutanotkun fyrri áskriftar',
        itemCaption__subscribedAndPaidForPlan: 'Skráð og greitt fyrir {{plan}} {{period}} áskrift',
        notFound: 'Yfirlit fannst ekki',
        tableHeader__amount: 'Upphæð',
        tableHeader__date: 'Dagsetning',
        title: 'Yfirlit',
        totalPaid: 'Samtals greitt',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Stjórna',
        actionLabel__newSubscription: 'Gerast áskrifandi',
        actionLabel__switchPlan: 'Skipta um áskrift',
        overview: undefined,
        tableHeader__edit: 'Breyta',
        tableHeader__plan: 'Áskrift',
        tableHeader__startDate: 'Upphafsdagur',
        title: 'Áskrift',
      },
      subscriptionsSection: {
        actionLabel__default: 'Stjórna',
      },
      switchPlansSection: {
        title: 'Skipta um áskrift',
      },
      title: 'Greiðslur',
    },
    connectedAccountPage: {
      formHint: 'Veldu þjónustuaðila til að tengja reikninginn þinn.',
      formHint__noAccounts: 'Engir tiltækir ytri reikningsþjónustuaðilar.',
      removeResource: {
        messageLine1: '{{identifier}} verður fjarlægt úr þessum reikningi.',
        messageLine2:
          'Þú munt ekki lengur geta notað þennan tengda reikning og öll háð eiginleikar munu ekki lengur virka.',
        successMessage: '{{connectedAccount}} hefur verið fjarlægt úr reikningnum þínum.',
        title: 'Fjarlægja tengdan reikning',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'Þjónustuaðilinn hefur verið bætt við reikninginn þinn',
      title: 'Bæta við tengdum reikningi',
    },
    deletePage: {
      actionDescription: 'Sláðu inn "Eyða reikningi" hér að neðan til að halda áfram.',
      confirm: 'Eyða reikningi',
      messageLine1:
        'Ertu viss um að þú viljir eyða reikningnum þínum? Sum tengd gögn kunna að verða varðveitt. Til að biðja um fullkomna eyðingu gagna, vinsamlegast hafðu samband við þjónustuver.',
      messageLine2: 'Þessi aðgerð er varanleg og óafturkræf.',
      title: 'Eyða reikningi',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Tölvupóstur sem inniheldur staðfestingarkóða verður sendur á þetta netfang.',
        formSubtitle: 'Sláðu inn staðfestingarkóðann sem sendur var á {{identifier}}',
        formTitle: 'Staðfestingarkóði',
        resendButton: 'Fékkstu ekki kóða? Senda aftur',
        successMessage: 'Netfangið {{identifier}} hefur verið bætt við reikninginn þinn.',
      },
      emailLink: {
        formHint: 'Tölvupóstur sem inniheldur staðfestingartengil verður sendur á þetta netfang.',
        formSubtitle: 'Smelltu á staðfestingartengilinn í tölvupóstinum sem sendur var á {{identifier}}',
        formTitle: 'Staðfestingartengill',
        resendButton: 'Fékkstu ekki tengil? Senda aftur',
        successMessage: 'Netfangið {{identifier}} hefur verið bætt við reikninginn þinn.',
      },
      enterpriseSSOLink: {
        formButton: 'Smelltu til að skrá þig inn',
        formSubtitle: 'Ljúktu innskráningu með {{identifier}}',
      },
      formHint: 'Þú þarft að staðfesta þetta netfang áður en hægt er að bæta því við reikninginn þinn.',
      removeResource: {
        messageLine1: '{{identifier}} verður fjarlægt úr þessum reikningi.',
        messageLine2: 'Þú munt ekki lengur geta skráð þig inn með þessu netfangi.',
        successMessage: '{{emailAddress}} hefur verið fjarlægt úr reikningnum þínum.',
        title: 'Fjarlægja netfang',
      },
      title: 'Bæta við netfangi',
      verifyTitle: 'Staðfesta netfang',
    },
    formButtonPrimary__add: 'Bæta við',
    formButtonPrimary__continue: 'Halda áfram',
    formButtonPrimary__finish: 'Ljúka',
    formButtonPrimary__remove: 'Fjarlægja',
    formButtonPrimary__save: 'Vista',
    formButtonReset: 'Hætta við',
    mfaPage: {
      formHint: 'Veldu aðferð til að bæta við.',
      title: 'Bæta við tveggja þrepa auðkenningu',
    },
    mfaPhoneCodePage: {
      backButton: 'Nota núverandi númer',
      primaryButton__addPhoneNumber: 'Bæta við símanúmeri',
      removeResource: {
        messageLine1: '{{identifier}} mun ekki lengur fá staðfestingarkóða við innskráningu.',
        messageLine2: 'Reikningurinn þinn gæti ekki verið eins öruggur. Ertu viss um að þú viljir halda áfram?',
        successMessage: 'SMS kóða tveggja þrepa auðkenning hefur verið fjarlægð fyrir {{mfaPhoneCode}}',
        title: 'Fjarlægja tveggja þrepa auðkenningu',
      },
      subtitle__availablePhoneNumbers:
        'Veldu núverandi símanúmer til að skrá fyrir SMS kóða tveggja þrepa auðkenningu eða bættu við nýju.',
      subtitle__unavailablePhoneNumbers:
        'Engin tiltæk símanúmer til að skrá fyrir SMS kóða tveggja þrepa auðkenningu, vinsamlegast bættu við nýju.',
      successMessage1:
        'Við innskráningu þarftu að slá inn staðfestingarkóða sem sendur er á þetta símanúmer sem viðbótar skref.',
      successMessage2:
        'Vistaðu þessa öryggiskóða og geymdu þá á öruggum stað. Ef þú missir aðgang að auðkennis tækinu þínu, getur þú notað öryggiskóða til að skrá þig inn.',
      successTitle: 'SMS kóða staðfesting virkjað',
      title: 'Bæta við SMS kóða staðfestingu',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Skanna QR kóða í staðinn',
        buttonUnableToScan__nonPrimary: 'Getur ekki skannað QR kóða?',
        infoText__ableToScan:
          'Settu upp nýja innskráningaraðferð í auðkennisforritinu þínu og skannaðu eftirfarandi QR kóða til að tengja það við reikninginn þinn.',
        infoText__unableToScan:
          'Settu upp nýja innskráningaraðferð í auðkennisforritinu þínu og sláðu inn lykilinn hér að neðan.',
        inputLabel__unableToScan1:
          'Gakktu úr skugga um að Tímatengdir eða Einnota lykilorð séu virkjað, og ljúktu síðan við að tengja reikninginn þinn.',
        inputLabel__unableToScan2:
          'Að öðrum kosti, ef auðkennisforritið þitt styður TOTP URI, geturðu einnig afritað fullan URI.',
      },
      removeResource: {
        messageLine1: 'Staðfestingarkóðar frá þessu auðkennisforriti verða ekki lengur nauðsynlegir við innskráningu.',
        messageLine2: 'Reikningurinn þinn gæti ekki verið eins öruggur. Ertu viss um að þú viljir halda áfram?',
        successMessage: 'Tveggja þrepa auðkenning með auðkennisforriti hefur verið fjarlægð.',
        title: 'Fjarlægja tveggja þrepa auðkenningu',
      },
      successMessage:
        'Tveggja þrepa auðkenning er nú virkjað. Við innskráningu þarftu að slá inn staðfestingarkóða frá þessu auðkennisforriti sem viðbótar skref.',
      title: 'Bæta við auðkennisforriti',
      verifySubtitle: 'Sláðu inn staðfestingarkóðann sem auðkennisforritið þitt bjó til',
      verifyTitle: 'Staðfestingarkóði',
    },
    mobileButton__menu: 'Valmynd',
    navbar: {
      account: 'Prófíll',
      apiKeys: 'API-lyklar',
      billing: 'Greiðslur',
      description: 'Stjórna reikningsupplýsingum þínum.',
      security: 'Öryggi',
      title: 'Reikningur',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} verður fjarlægt úr þessum reikningi.',
        title: 'Fjarlægja lykil',
      },
      subtitle__rename: 'Þú getur breytt nafni lykilsins til að auðvelda að finna hann.',
      title__rename: 'Endurnefna lykil',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Mælt er með að skrá þig út af öllum öðrum tækjum sem gætu hafa notað gamla lykilorðið þitt.',
      readonly:
        'Lykilorðið þitt er ekki hægt að breyta núna vegna þess að þú getur aðeins skráð þig inn í gegnum fyrirtækjatengingu.',
      successMessage__set: 'Lykilorðið þitt hefur verið sett.',
      successMessage__signOutOfOtherSessions: 'Öll önnur tæki hafa verið skráð út.',
      successMessage__update: 'Lykilorðið þitt hefur verið uppfært.',
      title__set: 'Setja lykilorð',
      title__update: 'Uppfæra lykilorð',
    },
    phoneNumberPage: {
      infoText:
        'SMS sem inniheldur staðfestingarkóða verður sent á þetta símanúmer. Skilaboð og gagnagjöld geta átt við.',
      removeResource: {
        messageLine1: '{{identifier}} verður fjarlægt úr þessum reikningi.',
        messageLine2: 'Þú munt ekki lengur geta skráð þig inn með þessu símanúmeri.',
        successMessage: '{{phoneNumber}} hefur verið fjarlægt úr reikningnum þínum.',
        title: 'Fjarlægja símanúmer',
      },
      successMessage: '{{identifier}} hefur verið bætt við reikninginn þinn.',
      title: 'Bæta við símanúmeri',
      verifySubtitle: 'Sláðu inn staðfestingarkóðann sem sendur var á {{identifier}}',
      verifyTitle: 'Staðfesta símanúmer',
    },
    plansPage: {
      title: 'Áskriftir',
    },
    profilePage: {
      fileDropAreaHint: 'Mælt stærð 1:1, allt að 10MB.',
      imageFormDestructiveActionSubtitle: 'Fjarlægja',
      imageFormSubtitle: 'Hlaða upp',
      imageFormTitle: 'Prófílmynd',
      readonly: 'Prófílupplýsingar þínar hafa verið veittar af fyrirtækjatengingu og er ekki hægt að breyta.',
      successMessage: 'Prófíllinn þinn hefur verið uppfærður.',
      title: 'Uppfæra prófíl',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Skrá út af tæki',
        title: 'Virk tæki',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Endurtengja',
        actionLabel__reauthorize: 'Heimila núna',
        destructiveActionTitle: 'Fjarlægja',
        primaryButton: 'Tengja reikning',
        subtitle__disconnected: 'Þessi reikningur hefur verið aftengdur.',
        subtitle__reauthorize:
          'Nauðsynlegar heimildir hafa verið uppfærðar, og þú gætir verið að upplifa takmarkaða virkni. Vinsamlegast endurheimilaðu þetta forrit til að forðast vandamál',
        title: 'Tengdir reikningar',
      },
      dangerSection: {
        deleteAccountButton: 'Eyða reikningi',
        title: 'Eyða reikningi',
      },
      emailAddressesSection: {
        destructiveAction: 'Fjarlægja netfang',
        detailsAction__nonPrimary: 'Setja sem aðal',
        detailsAction__primary: 'Ljúka staðfestingu',
        detailsAction__unverified: 'Staðfesta',
        primaryButton: 'Bæta við netfangi',
        title: 'Netföng',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Tengja reikning',
        title: 'Fyrirtækjareikningar',
      },
      headerTitle__account: 'Prófílupplýsingar',
      headerTitle__security: 'Öryggi',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Endurmynda',
          headerTitle: 'Öryggiskóðar',
          subtitle__regenerate:
            'Fáðu nýtt sett af öruggum öryggiskóðum. Fyrri öryggiskóðar verða eyttir og ekki hægt að nota.',
          title__regenerate: 'Endurmynda öryggiskóða',
        },
        phoneCode: {
          actionLabel__setDefault: 'Setja sem sjálfgefið',
          destructiveActionLabel: 'Fjarlægja',
        },
        primaryButton: 'Bæta við tveggja þrepa auðkenningu',
        title: 'Tveggja þrepa auðkenning',
        totp: {
          destructiveActionTitle: 'Fjarlægja',
          headerTitle: 'Auðkennisforrit',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Fjarlægja',
        menuAction__rename: 'Endurnefna',
        primaryButton: 'Bæta við lykli',
        title: 'Lyklar',
      },
      passwordSection: {
        primaryButton__setPassword: 'Setja lykilorð',
        primaryButton__updatePassword: 'Uppfæra lykilorð',
        title: 'Lykilorð',
      },
      phoneNumbersSection: {
        destructiveAction: 'Fjarlægja símanúmer',
        detailsAction__nonPrimary: 'Setja sem aðal',
        detailsAction__primary: 'Ljúka staðfestingu',
        detailsAction__unverified: 'Staðfesta símanúmer',
        primaryButton: 'Bæta við símanúmeri',
        title: 'Símanúmer',
      },
      profileSection: {
        primaryButton: 'Uppfæra prófíl',
        title: 'Prófíll',
      },
      usernameSection: {
        primaryButton__setUsername: 'Setja notendanafn',
        primaryButton__updateUsername: 'Uppfæra notendanafn',
        title: 'Notendanafn',
      },
      web3WalletsSection: {
        destructiveAction: 'Fjarlægja veski',
        detailsAction__nonPrimary: 'Setja sem aðal',
        primaryButton: 'Web3 veski',
        title: 'Web3 veski',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Veldu Solana-veski til að tengja við aðganginn þinn.',
          title: 'Bæta við Solana-veski',
        },
      },
    },
    usernamePage: {
      successMessage: 'Notendanafnið þitt hefur verið uppfært.',
      title__set: 'Setja notendanafn',
      title__update: 'Uppfæra notendanafn',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} verður fjarlægt úr þessum reikningi.',
        messageLine2: 'Þú munt ekki lengur geta skráð þig inn með þessu web3 veski.',
        successMessage: '{{web3Wallet}} hefur verið fjarlægt úr reikningnum þínum.',
        title: 'Fjarlægja web3 veski',
      },
      subtitle__availableWallets: 'Veldu web3 veski til að tengja við reikninginn þinn.',
      subtitle__unavailableWallets: 'Engin tiltæk web3 veski.',
      successMessage: 'Veskið hefur verið bætt við reikninginn þinn.',
      title: 'Bæta við web3 veski',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Skrá inn',
      actionText: 'Áttu nú þegar aðgang?',
      formButton: 'Skrá sig á biðlista',
      subtitle: 'Sláðu inn netfangið þitt og við látum þig vita þegar staða þín er tilbúin',
      title: 'Skrá sig á biðlista',
    },
    success: {
      message: 'Þú verður áframsendur fljótlega...',
      subtitle: 'Við höfum samband þegar staða þín er tilbúin',
      title: 'Takk fyrir að skrá sig á biðlistann!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Tengjast með {{walletName}}',
    continue: 'Halda áfram með {{walletName}}',
    noneAvailable:
      'Engin Solana Web3 veski fundust. Vinsamlegast settu upp Web3-stutt {{ solanaWalletsLink || link("wallet extension") }}.',
  },
} as const;
